import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isStaff} from '@github-ui/stats'

export type StaffDebugCommand =
  | {kind: 'copy-debug-log'}
  | {kind: 'download-debug-log'}
  | {kind: 'report-bug'; title: string}
  | {kind: 'invalid'; message: string}

export interface StaffDebugCommandDescriptor {
  key: string
  displayKey?: string
  description: string
  appendSpace?: boolean
  /** Placeholder for the command's argument, rendered muted after the key. */
  argumentHint?: string
}

export const STAFF_DEBUG_COMMANDS: StaffDebugCommandDescriptor[] = [
  {key: '/debug', description: 'Copy this session’s debug log to the clipboard'},
  {
    key: '/collect-debug-logs',
    appendSpace: true,
    description: 'Collect this session’s debug logs',
  },
  {key: '/report-bug', argumentHint: 'title', description: 'Report a bug — the argument becomes the issue title'},
]

export const COLLECT_DEBUG_LOG_OPTIONS: StaffDebugCommandDescriptor[] = [
  {
    key: '/collect-debug-logs download',
    displayKey: 'download',
    description: 'Download the debug log archive',
  },
]

export function areStaffDebugCommandsEnabled(): boolean {
  return isStaff() && isFeatureEnabled('one_copilot_staff_debug_slash_commands')
}

/**
 * Parse `text` as a staff debug slash command. Returns `null` when it is not one
 * of them, or when the staff gate is closed, so the caller dispatches a normal
 * chat turn.
 */
export function parseStaffDebugCommand(text: string): StaffDebugCommand | null {
  if (!areStaffDebugCommandsEnabled()) return null

  const trimmed = text.trim()
  const firstSpace = trimmed.search(/\s/)
  const name = (firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace)).toLowerCase()
  const arg = (firstSpace === -1 ? '' : trimmed.slice(firstSpace + 1)).trim()

  switch (name) {
    case '/debug':
      if (arg) return {kind: 'invalid', message: '/debug does not take an argument.'}
      return {kind: 'copy-debug-log'}
    case '/collect-debug-logs':
      if (arg === '' || arg.toLowerCase() === 'download') return {kind: 'download-debug-log'}
      return {
        kind: 'invalid',
        message: `Unknown /collect-debug-logs option “${arg}”. Try /collect-debug-logs download.`,
      }
    case '/report-bug':
      if (!arg) return {kind: 'invalid', message: 'Add a bug title after /report-bug.'}
      return {kind: 'report-bug', title: arg}
    default:
      return null
  }
}
